import tokens from '@kluein/design-tokens';
import { Anchor, Avatar, Group, Text, type AvatarProps } from '@mantine/core';
import { useEffect, type ReactNode } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { APP_V1_BASEURL } from 'lib/urls';
import store from 'store';
import TEST_IDS from 'test-ids';

import type { UserType } from 'api/api.types';
import type { Dispatch, RootState } from 'store/store.types';

type UserAvatarProps = {
  email?: UserType['email'];
  name?: UserType['name'];
  userId: UserType['id'];
  src?: string;
  showName?: boolean;
  linkTo?: 'email' | 'profile';
  showBorder?: boolean;
} & Omit<AvatarProps, 'src' | 'alt' | 'name'>;

const UserAvatar = ({
  email,
  linkTo,
  name,
  src,
  showName = false,
  userId,
  showBorder = true,
  style,
  ...rest
}: UserAvatarProps): ReactNode => {
  ({ email, name, src } = useSelector<
    RootState,
    Partial<Pick<UserType, 'email' | 'name'> & { src: UserType['image'] }>
  >((state) => {
    const userInStore = store.select.users.byId(state).get(userId?.toString());
    const {
      email: storeEmail,
      name: storeName,
      image: storeSrc,
    } = userInStore || {};
    return {
      email: email || storeEmail,
      name: name || storeName,
      src: src || storeSrc,
    };
  }, shallowEqual));
  const {
    users: { loadOrFetchUserById },
  } = useDispatch<Dispatch>();

  useEffect(() => {
    if (!name) {
      loadOrFetchUserById(userId);
    }
  }, [loadOrFetchUserById, name, userId]);

  if (!name) {
    return null;
  }

  const borderStyle = showBorder
    ? {
        border: `${tokens.borderWidth.small} solid ${tokens.color.neutral.black.main}`,
      }
    : {};

  const AvatarComponent = (
    <Avatar
      src={src || null}
      alt={name}
      title={name}
      name={name}
      color="initials"
      data-test-id={TEST_IDS.userAvatar.avatar}
      style={{ flexShrink: 0, ...borderStyle, ...style }}
      {...rest}
    />
  );

  const NameText = (
    <Text fw={600} size="sm" truncate>
      {name}
    </Text>
  );

  const LinkWrapper = ({ children }: { children: ReactNode }): ReactNode => {
    switch (linkTo) {
      case 'profile':
        return (
          <Anchor
            data-test-id={TEST_IDS.userAvatar.anchor.profile}
            href={`${APP_V1_BASEURL}/users/${userId}`}
            underline="never"
            c="inherit"
          >
            {children}
          </Anchor>
        );
      case 'email':
        if (!email) {
          return <>{children}</>;
        }
        return (
          <Anchor
            data-test-id={TEST_IDS.userAvatar.anchor.email}
            href={`mailto:${email}`}
            underline="never"
            c="inherit"
          >
            {children}
          </Anchor>
        );
      default:
        return <>{children}</>;
    }
  };

  return showName ? (
    <Group gap="xs" wrap="nowrap" data-test-id={TEST_IDS.userAvatar.box}>
      {AvatarComponent}
      <LinkWrapper>{NameText}</LinkWrapper>
    </Group>
  ) : (
    <LinkWrapper>{AvatarComponent}</LinkWrapper>
  );
};

export default UserAvatar;
